import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts"

// Gauge sweeps from 225deg (value=min) to -45deg (value=max), going clockwise.
const START_ANGLE = 225
const END_ANGLE = -45

// Per-boundary radial distance (px, outward from the axis line) — tuned
// individually because a single uniform distance reads unevenly across the arc.
const BOUNDARY_LABEL_OFFSETS = {
  0:   { distance: 18 },
  15:  { distance: 19 },
  50:  { distance: 13 },
  85:  { distance: 19 },
  100: { distance: 18 }
}

export default class extends Controller {
  static values = { value: Number, max: Number, colors: Array, label: String }

  connect() {
    this.chart = echarts.init(this.element)
    this.chart.setOption(this.buildOption())
    // The chart has no interactive elements; let clicks/hover pass through to
    // the wrapping <a> so it shows a pointer cursor and remains clickable.
    this.chart.getDom().style.pointerEvents = "none"

    this.resizeHandler = () => {
      this.chart.resize()
      this.chart.setOption({ graphic: this.boundaryLabelGraphics() })
    }
    window.addEventListener("resize", this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    this.chart.dispose()
  }

  get tierBoundaries() {
    return [
      { value: 0, color: this.colorsValue[0][1] },
      ...this.colorsValue.map(([ stop, color ]) => ({ value: Math.round(stop * this.maxValue), color }))
    ]
  }

  boundaryLabelGraphics() {
    const size = Math.min(this.element.clientWidth, this.element.clientHeight)
    const cx = this.element.clientWidth / 2
    const cy = this.element.clientHeight / 2
    const radius = size / 2 * 0.62

    return this.tierBoundaries.map(({ value, color }) => {
      const ratio = value / this.maxValue
      const angleDeg = START_ANGLE + (END_ANGLE - START_ANGLE) * ratio
      const angleRad = (angleDeg * Math.PI) / 180
      const offset = BOUNDARY_LABEL_OFFSETS[value]?.distance ?? 30
      const r = radius + offset

      return {
        type: "text",
        x: cx + r * Math.cos(angleRad),
        y: cy - r * Math.sin(angleRad),
        style: {
          text: `${value}%`,
          fill: color,
          fontSize: 14,
          fontWeight: "bold",
          align: "center",
          verticalAlign: "middle"
        },
        silent: true
      }
    })
  }

  buildOption() {
    return {
      graphic: this.boundaryLabelGraphics(),
      series: [
        {
          type: "gauge",
          min: 0,
          max: this.maxValue,
          radius: "62%",
          startAngle: START_ANGLE,
          endAngle: END_ANGLE,
          axisLine: {
            lineStyle: {
              width: 14,
              color: this.colorsValue.map(([ stop, color ]) => [ stop, color ])
            }
          },
          pointer: {
            length: "70%",
            itemStyle: { color: "auto" }
          },
          axisTick: {
            distance: -15,
            length: 6,
            lineStyle: {
              color: "#fff",
              width: 1
            }
          },
          splitLine: { show: false },
          axisLabel: { show: false },
          title: { show: false },
          animation: false,
          detail: {
            valueAnimation: false,
            formatter: "{value}%",
            color: "auto",
            fontSize: 22,
            offsetCenter: [ 0, "100%" ]
          },
          data: [ { value: this.valueValue, name: this.labelValue } ]
        }
      ]
    }
  }
}
